import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Send, Save, Smartphone, Mail } from "lucide-react";

const INPUT_STYLE = { background: "var(--vault-bg)", border: "1px solid var(--vault-border)" };
const INPUT_CLS = "w-full px-3 py-2.5 rounded outline-none text-sm";

function TestRow({ channel, placeholder }) {
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/sender-settings/test", { channel, to: to.trim() });
      if (data.ok) toast.success(channel === "sms" ? "Test SMS sent" : "Test email sent");
      else toast.error(data.error || "Sending failed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sending failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex gap-2 mt-2">
      <input value={to} onChange={(e) => setTo(e.target.value)} placeholder={placeholder} className={`${INPUT_CLS} flex-1`} style={INPUT_STYLE} data-testid={`sender-test-${channel}-to`} />
      <button type="button" onClick={run} disabled={busy || !to.trim()} className="btn-ghost inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm" data-testid={`sender-test-${channel}-btn`}>
        {busy ? <span className="spin" /> : <Send className="w-3.5 h-3.5" />} Test
      </button>
    </div>
  );
}

export default function SenderSection() {
  const [sms, setSms] = useState("");
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/sender-settings").then((r) => { setSms(r.data.sms_sender); setEmail(r.data.email_from); }).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/sender-settings", { sms_sender: sms, email_from: email });
      setSms(data.sms_sender);
      setEmail(data.email_from);
      toast.success("Senders saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="rounded-lg p-8 border space-y-6" style={{ background: "var(--vault-surface)", borderColor: "var(--vault-border)" }} data-testid="sender-section">
      <div>
        <h2 className="font-display text-2xl mb-2">Message senders</h2>
        <p className="text-sm text-[var(--vault-text-muted)]">Who access codes, alerts and summaries come from. Save, then send a test.</p>
      </div>
      <div>
        <label className="overline block text-[var(--vault-text-muted)] mb-2 inline-flex items-center gap-1"><Smartphone className="w-3 h-3" /> SMS sender</label>
        <input value={sms} onChange={(e) => setSms(e.target.value)} placeholder="Verifine" maxLength={16} className={`${INPUT_CLS} font-mono-vault`} style={INPUT_STYLE} data-testid="sender-sms-input" />
        <p className="text-[10px] text-[var(--vault-text-faint)] mt-1">A name of up to 11 letters (recipients can't reply) or a Twilio number (+...).</p>
        <TestRow channel="sms" placeholder="Send a test SMS to +351..." />
      </div>
      <div>
        <label className="overline block text-[var(--vault-text-muted)] mb-2 inline-flex items-center gap-1"><Mail className="w-3 h-3" /> Email sender</label>
        <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Verifine Wines <noreply@www.verifinewines.com>" className={INPUT_CLS} style={INPUT_STYLE} data-testid="sender-email-input" />
        <p className="text-[10px] text-[var(--vault-text-faint)] mt-1">Must use a domain or sender verified in SendGrid.</p>
        <TestRow channel="email" placeholder="Send a test email to..." />
      </div>
      <div className="flex justify-end pt-4 border-t" style={{ borderColor: "var(--vault-border)" }}>
        <button type="button" onClick={save} disabled={saving} className="btn-gold inline-flex items-center gap-2 px-6 py-2.5 rounded-md text-sm" data-testid="sender-save-btn">
          {saving ? <span className="spin" /> : <Save className="w-4 h-4" />} Save senders
        </button>
      </div>
    </section>
  );
}
